import { findSegment, interpolateWithSegment } from './data';

/** An attachment interval can be a flight, even before its release/arrival key. */
export function hasAttachmentFlight(track, t) {
    if (isPlayerTrackId(track?.id)) return false;
    const keys = [...(track?.attach || [])].sort((a, b) => a.time - b.time);
    const active = keys.filter(k => k.time <= t).pop();
    const next = keys.find(k => k.time > t);
    if (!active || !next || active.value === next.value) return false;
    if (next.value && next.value !== 'none') return true;
    // A detach endpoint needs a keyed landing, just as the effective sampler does.
    return (track.position || []).some(k => Math.abs(k.time - next.time) < 1e-6);
}

/**
 * SAMPLING — where a track actually is at a given time.
 *
 * Position keys alone do not tell the whole story for anything that can be
 * ATTACHED (a ball, a zone): while it is attached it does not follow its own
 * keys at all, it rides the thing it is attached to. So the effective position
 * has to blend three things — the attachment keys, the attached object's own
 * movement, and the item's own position keys — and that logic lives here so it
 * can be reasoned about and tested without a 3D scene.
 */


/** Interpolated player position (x, y, z) at time t from the movement track keys. */
export function sampleTrackPosition(track, t) {
    const keys = track?.position || [];
    if (keys.length === 0) return null;
    if (keys.length === 1) return keys[0].position;
    let k0 = null, k1 = null;
    for (const k of keys) {
        if (k.time <= t) k0 = k; else { k1 = k; break; }
    }
    if (!k0) k0 = keys[0];
    if (!k1) k1 = keys[keys.length - 1];
    const span = k1.time - k0.time;
    if (span <= 0) return k1.position;
    const f = (t - k0.time) / span;
    // Follow the segment's trajectory (lateral curve + arch) between the pair.
    return interpolateWithSegment(k0, k1, f, findSegment(track, k0.time, k1.time));
}

/**
 * Yaw (radians) that turns someone standing at `pos` to face the CLOSEST BALL,
 * or null when there is no ball to face.
 *
 * Balls are sampled in their EFFECTIVE positions, so a ball being carried is
 * looked at where it actually is (on its carrier) rather than at its own key.
 *
 * This is the default facing for a player with no direction of their own — no
 * travel along their path, no manual or attach look target — so they keep their
 * eyes on the ball instead of holding whatever axis they happened to be on.
 */
export function nearestBallYaw(tracks, pos, t) {
    const all = tracks || [];
    let best = null;
    let bestDist = Infinity;
    for (const track of all) {
        const isBall = track.type === 'ball' || String(track.id || '').startsWith('ball:');
        if (!isBall) continue;
        const ballPos = sampleEffectivePosition(track, t, all);
        if (!ballPos) continue;
        const dx = ballPos[0] - pos[0];
        const dz = ballPos[2] - pos[2];
        const d = Math.hypot(dx, dz);
        if (d < 1e-4) continue;   // standing on top of it — there is no yaw to take
        if (d < bestDist) { bestDist = d; best = Math.atan2(dx, dz); }
    }
    return best;
}

/** A player track id: 'home:3' / 'away:11'. Items are 'cone:id', 'ball:id'… */
export function isPlayerTrackId(id) {
    return typeof id === 'string' && /^(home|away):\d+$/.test(id);
}

export const MARK_DEFAULT_DIST = 1.5;   // the gap a mark key from before the compass gets

/**
 * The eight bearings a marker can hold around the player they mark, CLOCKWISE
 * from the marked player's front. "Front" is the goal-side axis: the direction
 * the MARKED player's team attacks, fixed for the whole clip, so the bearing you
 * pick on the compass is the bearing you see — it does not swing around when the
 * marked player turns.
 */
export const MARK_DIRS = Object.freeze(['front', 'frontRight', 'right', 'backRight',
    'back', 'backLeft', 'left', 'frontLeft']);

/** The side half of an attach ref ('away:3') — the marked player's team. */
export function sideOfAttachRef(ref) {
    return typeof ref === 'string' && ref.startsWith('away:') ? 'away' : 'home';
}

/**
 * The offset a marker holds from the marked player, in pitch x/z.
 *
 * Home attacks +x, away attacks −x (verified against the saved demo scenario:
 * home players start at negative x and travel toward positive). The marked
 * player's RIGHT hand is then +z for home, −z for away — the pair (front, right)
 * is the compass dial seen from above, front at the top.
 */
export function markOffset(dir, dist, markedSide) {
    const fx = markedSide === 'away' ? -1 : 1;          // front, along x
    const d = Math.max(0, Number(dist) || 0);
    // The bearing is continuous; old integer keys remain the eight compass points.
    // The sampler is asked for every instant between two keys, and a marker who
    // changes side has to travel around the marked player to get there — not
    // appear on the other side of them. cos/sin over dir × 45° reproduces the
    // eight-point table exactly (the diagonals land at 0.707·d, so the gap is
    // the same distance whichever way they stand).
    const a = (Number(dir) || 0) * Math.PI / 4;         // 0 = front, +1 = their right
    const c = Math.cos(a), s = Math.sin(a);
    // front f = (fx, 0), right r = (0, fx), in the (x, z) ground plane.
    // cos(π) is not exactly −1 and sin(π) is not exactly 0: a saved scenario
    // would fill up with 1e-16 noise, and a test that says "2 m to the right"
    // would never match. Anything that small IS zero.
    const clean = (v) => (Math.abs(v) < 1e-9 ? 0 : v);
    return [clean(fx * c * d), 0, clean(fx * s * d)];
}

/**
 * The compass read BACKWARDS. Given where the marker actually stands and where
 * the player they mark stands, return the {dir, dist} the dial would have to be
 * set to — a continuous bearing and distance clamped to 0.5–20 metres.
 *
 * This exists so the compass can OPEN on the geometry the coach is already
 * looking at instead of on a placeholder. Marking an opponent who happens to
 * be 3 m off your right shoulder is then one tap on Set, not a bearing hunt:
 * The inverse preserves freely placed spots when reopening the dial.
 *
 * The inverse of markOffset on purpose: the same fx sign, the same (x, z)
 * ground plane, the same clockwise bearings. If one changes, this has to
 * change with it — which is why both live here and nowhere else.
 */
export function markBearing(markerPos, markedPos, markedSide, opts = {}) {
    const minDist = opts.minDist ?? 0.5;
    const maxDist = opts.maxDist ?? 20;
    const step = opts.step ?? 0;
    if (!Array.isArray(markerPos) || !Array.isArray(markedPos)) return null;
    const ox = markerPos[0] - markedPos[0];
    const oz = (markerPos[2] ?? 0) - (markedPos[2] ?? 0);
    const d = Math.hypot(ox, oz);
    // Standing on top of the marked player has no bearing — the dial would
    // invent one out of float noise. Let the caller keep its default gap.
    if (!(d > 1e-6)) return null;
    const fx = markedSide === 'away' ? -1 : 1;
    let dir = Math.atan2(fx * oz, fx * ox) / (Math.PI / 4);
    dir = ((dir % 8) + 8) % 8;
    if (Math.abs(dir - Math.round(dir)) < 1e-10) dir = Math.round(dir) % 8;
    const dist = Math.min(maxDist, Math.max(minDist, step > 0 ? Math.round(d / step) * step : d));
    return { dir, dist };
}

/**
 * How long a marker should take to walk from one bearing to another: the ARC
 * they have to cover, at a jog. A quarter turn at 1 m is half a second; a full
 * change of side at 4 m is a couple of strides more. Clamped so a mark never
 * turns in a blink or drifts like a tanker.
 */
export function markTurnSeconds(fromDir, toDir, dist) {
    let delta = (Number(toDir) || 0) - (Number(fromDir) || 0);
    while (delta > 4) delta -= 8;
    while (delta < -4) delta += 8;
    const arc = Math.abs(delta) * (Math.PI / 4) * Math.max(Number(dist) || 0, 0.5);
    return Math.min(3, Math.max(0.5, arc / 5));
}

/**
 * How long a marker should take to REACH a marking spot that is not a turn around
 * their current leader but a move across the pitch - switching WHO they mark.
 *
 * markTurnSeconds measures an ARC (bearing change at a fixed gap), so it is the wrong
 * tool for a straight-line re-target: it returns its floor whenever the bearings happen
 * to match, which is exactly when a re-mark crosses the most ground. Same ~5 m/s pace
 * and the same 0.5-3 s clamp, so a re-mark and a re-aim read as the same athlete moving.
 */
export function markTravelSeconds(distanceM) {
    const d = Math.max(Number(distanceM) || 0, 0);
    return Math.min(3, Math.max(0.5, d / 5));        // ≈5 m/s: a running pace
}

/**
 * The mark key in force for `trackId` at time `t`, or null when none is.
 *
 * The compass reads it, so re-marking the player they already mark opens on the
 * spot the marker is standing at NOW: changing sides becomes one tap on the dial
 * instead of a guess at where they currently are.
 */
export function markKeyAt(allTracks, trackId, t) {
    const track = (allTracks || []).find(tr => tr && tr.id === trackId);
    if (!track || !Array.isArray(track.attach)) return null;
    let active = null;
    for (const k of [...track.attach].sort((a, b) => (a?.time ?? 0) - (b?.time ?? 0))) {
        if (!k || typeof k.time !== 'number') continue;
        if (!k.value || k.value === 'none') { active = null; continue; }   // released
        if (k.time <= t + 1e-6) active = k;
    }
    return active;
}

/**
 * Where a MARKING player actually is at time t, or null when nothing about the
 * mark changes their authored path (before the mark starts, after a release with
 * no gap to keep, a marker who is not a player, a cycle).
 *
 * The rule in one sentence, because a coach has to be able to predict it: while
 * a mark is on, the marker stands at the chosen bearing and distance from the
 * marked player — their own keys are parked, and on release they carry on from
 * wherever the mark left them.
 */
function markPosition(track, t, allTracks, visiting) {
    const seen = visiting || new Set();
    if (seen.has(track.id)) return null;   // cycle: sample this one from its own keys
    const sorted = [...track.attach]
        .filter(k => k && typeof k.time === 'number')
        .sort((a, b) => a.time - b.time);
    let active = null, index = -1;
    for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].time <= t + 1e-6) { active = sorted[i]; index = i; } else break;
    }
    if (!active) return null;                       // the mark has not been requested yet
    const own = sampleTrackPosition(track, t);
    const down = new Set(seen); down.add(track.id);
    const leaderTrackOf = (ref) => (isPlayerTrackId(ref)
        ? (allTracks || []).find(tr => tr && tr.id === ref) : null);
    // The bearing and the gap: written on every mark key, defaulted for keys
    // authored before the compass existed so old scenarios still play — front,
    // 1.5 m — without a silent rewrite of saved data.
    const bearingOf = (k) => (Number.isFinite(k.dir) ? k.dir : 0);
    const gapOf = (k) => (Number.isFinite(k.dist) ? k.dist : MARK_DEFAULT_DIST);
    const markAt = (k, tt) => {
        const leader = leaderTrackOf(k.value);
        if (!leader) return null;
        const at = sampleEffectivePosition(leader, tt, allTracks, down);
        if (!at) return null;
        const off = markOffset(bearingOf(k), gapOf(k), sideOfAttachRef(k.value));
        return [at[0] + off[0], (at[1] ?? 0) + off[1], at[2] + off[2]];
    };
    if (active.value && active.value !== 'none') {
        // THE BEARING SWEEPS. Two mark keys that disagree about which side to
        // stand on are not a jump — between them the marker travels the short
        // way round the marked player, easing out of one spot and into the next.
        // A teleport would read as a foul: a defender who was on the attacker's
        // right hand suddenly appears behind their back, and the running
        // animation never gets a displacement to run on.
        let dir = bearingOf(active), dist = gapOf(active);
        const after = sorted.slice(index + 1);
        const next = after[0];
        if (next?.value && next.value !== 'none' && next.value !== active.value && next.time > active.time) {
            // Changing opponent is a journey between marking spots. Both spots
            // follow their moving opponents, so the endpoint meets the new mark
            // exactly at its key, including when seeking backwards through it.
            const from = markAt(active, t);
            const to = markAt(next, t);
            if (from && to) {
                const u = Math.min(1, Math.max(0, (t - active.time) / (next.time - active.time)));
                const e = u * u * (3 - 2 * u);
                return from.map((v, axis) => v + (to[axis] - v) * e);
            }
        }
        // A release in between ends the mark, so the sweep must not run through
        // it — the marker would be turning toward a spot they were already
        // handed back the pen to leave.
        const release = after.find(k => !k.value || k.value === 'none');
        if (next && next.value === active.value && next.time > active.time + 1e-9 && (!release || release.time > next.time)) {
            const u = Math.min(1, Math.max(0, (t - active.time) / (next.time - active.time)));
            const e = u * u * (3 - 2 * u);              // ease in and out of the turn
            // The short way round: front → back-left passes through left, not
            // through back. Eight points on a compass, so ±4 is the halfway mark.
            let delta = bearingOf(next) - dir;
            while (delta > 4) delta -= 8;
            while (delta < -4) delta += 8;
            dir += delta * e;
            dist += (gapOf(next) - dist) * e;
        }
        const held = markAt({ value: active.value, dir, dist }, t);
        return held || own || null;
    }
    // RELEASED: keep the gap the mark had already caused, then carry on. Without
    // this they would snap back to a path laid down before the mark happened.
    const earlier = sorted.slice(0, index).filter(k => k.value && k.value !== 'none').pop();
    if (!earlier) return null;                       // never marked anything
    const atRelease = markAt(earlier, active.time);
    const ownAtRelease = sampleTrackPosition(track, active.time);
    if (!atRelease || !ownAtRelease || !own) return own || null;
    // The gap is a GROUND gap: x and z only. Nobody marks from four metres up,
    // and dragging the height too would tilt their path for no reason.
    return [own[0] + (atRelease[0] - ownAtRelease[0]), own[1] ?? 0,
        own[2] + (atRelease[2] - ownAtRelease[2])];
}

/**
 * EFFECTIVE position of a track at time t (x, y, z) or null.
 *
 * A PLAYER MARKING ANOTHER PLAYER is handled first, and it works the opposite way
 * round from a ball riding a player. A ball has no path of its own while it is
 * attached — the attachment OVERWRITES its position. A marker holds a BEARING AND A
 * GAP: their own movement keys are parked for as long as the mark key is in force,
 * and the sampler keeps them at the chosen compass point and distance from the marked
 * player. So the shape the two were in when the coach asked is preserved, and whatever
 * the marked player does — a run, a curve, a stop — the marker answers it at that same
 * gap.
 *
 *   effective(t) = marked(t) + markOffset(bearing, gap, side)
 *
 * The bearing SWEEPS: two mark keys that disagree about which side to stand on are not
 * a jump, they travel the short way round the marked player (see markPosition).
 *
 * This is why the item card shuts ↑↓ for a player whose mark key is in force: the
 * marker's height comes from the marked player (at[1] + off[1]), so a height written on
 * the marker's own key is a number nothing draws.
 *
 * `visiting` guards chains (A marks B marks C) and cycles (A marks B marks A): a track
 * already on the way down is sampled from its OWN keys, so the recursion always
 * terminates.
 *
 * (This block used to describe a FOLLOW model — own(t) + [leader(t) − leader(t_follow)]
 * — and the word survives in the item-attach toast, where it means riding a carrier. No
 * such rule is implemented: the player rule is the bearing-and-gap one above, in
 * markPosition.)
 *
 * For a BALL or ZONE with attachment keys, the attachment OVERWRITES its position:
 *   - No active attach key (or the active key is 'none') → the item's own
 *     interpolated movement position.
 *   - Attached to player P with no upcoming key → follows P's interpolated
 *     position.
 *   - Attached to P with a next key (attach to Q, or detach) → interpolates
 *     from P's location at the attach moment to the next state's location at
 *     the next key's time (Q's location, or the ball's own position if it
 *     detaches). This gives the smooth "throw/pass between players" motion.
 *
 * A DETACH is the one case where the item's own keys are not enough on their
 * own: they describe a path laid down before the release, and interpolating from
 * them would fly the ball back to wherever its last key happened to be. So a
 * detach key becomes the START of the interpolation — the item continues from
 * where it was actually released (the carrier's feet, or the position key the
 * user placed at that instant), gliding to its next own key over that key's own
 * segment settings, and simply staying at the release point when no landing has
 * been keyed yet.
 */
export function sampleEffectivePosition(track, t, allTracks, visiting) {
    const attachKeys = track?.attach || [];
    const isBall = typeof track?.id === 'string' && track.id.startsWith('ball:');
    const isZone = typeof track?.id === 'string' && track.id.startsWith('zone:');
    const isConeOrTarget = typeof track?.id === 'string'
        && (track.id.startsWith('cone:') || track.id.startsWith('target:'));
    // A player MARKING another player: their own keys are parked and they hold
    // the chosen bearing and gap. Checked before the ball/zone branch because
    // the rules are deliberately different — one overwrites, one rides along.
    if (isPlayerTrackId(track?.id) && attachKeys.length > 0) {
        const marked = markPosition(track, t, allTracks, visiting);
        if (marked) return marked;
    }
    // Balls, zones, CONES and TARGETS support attachment. A ball is held
    // slightly IN FRONT of the attached object (0.6 m, dribble lead); a zone, a
    // cone and a target are centred ON it.
    //
    // Cones and targets were left out of this list once, and nothing else in
    // the app noticed: the item menu offers 📎 Attach to every non-player, the
    // strip grows an Attachment lane for them, the pitch lets them be dropped
    // on a carrier — and the sampler then ignored the key, so the cone sat
    // still while the midfielder it was pinned to ran away. A door that does
    // nothing when you open it is worse than no door at all.
    const attachSupported = isBall || isZone || isConeOrTarget;
    const attachOffset = isBall ? 0.6 : 0;
    if (attachSupported && attachKeys.length > 0) {
        const sorted = [...attachKeys].sort((a, b) => a.time - b.time);
        const ownPos = (tt) => sampleTrackPosition(track, tt);
        const ownKeyAt = (tt) => (track.position || []).find(k => Math.abs(k.time - tt) < 1e-6);
        const ownAfter = (tt) => (track.position || []).filter(k => k.time > tt);
        // active = the holding key at/just before t; prev = the key before it.
        let active = null, prev = null;
        for (const k of sorted) {
            if (k.time <= t) { prev = active; active = k; } else break;
        }
        const attached = (k) => (k && k.value && k.value !== 'none') ? k.value : null;
        const playerPos = (trackId, tt) => carrierOf(trackId, tt);
        // The carrier, as they are ACTUALLY moving. A player who is marking
        // another player has their own keys parked — the leader carries them —
        // and a ball or zone riding the authored path stayed down the touchline
        // while the player it was glued to ran across the pitch. Same for the
        // direction: 0.6 m "in front" means in front of where they are going.
        const carrierOf = (trackId, tt) => {
            const pTrack = (allTracks || []).find(tr => tr && tr.id === trackId);
            if (!pTrack) return null;
            const seenSoFar = visiting || new Set();
            // A chain that comes back to this track (zone → ball → zone) stops
            // here rather than recursing: the far end is sampled from its own
            // keys, exactly as the player-marking cycle guard does.
            if (seenSoFar.has(trackId) || trackId === track.id) return null;
            const down = new Set(seenSoFar);
            down.add(track.id);
            return sampleEffectivePosition(pTrack, tt, allTracks, down);
        };
        // Forward direction (unit x/z) of a carrier at time tt, used to hold the
        // ball slightly in FRONT of the attached player.
        const forwardDir = (trackId, tt) => {
            const eps = 0.04;
            const a = carrierOf(trackId, tt - eps);
            const b = carrierOf(trackId, tt + eps);
            if (a && b) {
                const ex = b[0] - a[0], ez = b[2] - a[2];
                const elen = Math.hypot(ex, ez);
                if (elen > 1e-4) return [ex / elen, ez / elen];
            }
            // Fallback: the authored key span. Where the carrier is genuinely
            // standing still at the end of a run this keeps the dribble lead
            // pointing the way they arrived, instead of snapping it back.
            const pTrack = (allTracks || []).find(tr => tr && tr.id === trackId);
            const keys = pTrack?.position || [];
            if (keys.length === 0) return null;
            let k0 = null, k1 = null;
            for (const k of keys) {
                if (k.time <= tt) k0 = k; else { k1 = k; break; }
            }
            if (!k0 && keys[0]) k0 = keys[0];
            if (!k1 && keys.length > 1) k1 = keys[keys.length - 1];
            if (!k0 || !k1 || k1.time <= k0.time) return null;
            const span = k1.time - k0.time;
            const f = Math.min(1, Math.max(0, (tt - k0.time) / span));
            const seg = findSegment(pTrack, k0.time, k1.time);
            const sep = Math.min(0.02, Math.max(span * 1e-3, 1e-4));
            const pa = interpolateWithSegment(k0, k1, Math.max(0, f - sep), seg);
            const pb = interpolateWithSegment(k0, k1, Math.min(1, f + sep), seg);
            const dx = pb[0] - pa[0], dz = pb[2] - pa[2];
            const len = Math.hypot(dx, dz);
            if (len < 1e-4) return null;
            return [dx / len, dz / len];
        };
        // Ball held slightly in front of the attached player (0.6 m along the
        // player's forward/travel direction, so it leads the dribble); a zone
        // is centred exactly on the attached object (offset 0).
        // NOTE: forwardDir returns a 2-element [dirX, dirZ] array — z is index 1.
        const offsetPos = (pos, dir) => {
            if (!pos) return null;
            if (!dir) return pos;
            return [pos[0] + dir[0] * attachOffset, pos[1] ?? 0, pos[2] + dir[1] * attachOffset];
        };
        const carrierPos = (value, time) => offsetPos(playerPos(value, time), forwardDir(value, time));
        const interpolate = (from, to, start, end) => {
            const f = Math.min(1, Math.max(0, (t - start) / Math.max(1e-6, end - start)));
            return interpolateWithSegment({ position: from }, { position: to }, f, findSegment(track, start, end));
        };
        if (!active) {
            const first = sorted[0];
            const start = (track.position || []).filter(k => k.time < first.time).pop();
            const target = attached(first);
            const arrival = target && carrierPos(target, first.time);
            // An initial free ball can travel to its first attachment key.
            if (start && arrival && t >= start.time) return interpolate(start.position, arrival, start.time, first.time);
            return ownPos(t);
        }

        const dest = attached(active);     // the holding state from active.time on
        const tN = active.time;
        // Attachment keys are arrival endpoints, not delayed flight triggers.
        let nextKey = null;
        for (const k of sorted) { if (k.time > tN) { nextKey = k; break; } }
        const prevDest = prev ? attached(prev) : null;
        const nextDest = attached(nextKey);
        if (nextKey && nextDest !== dest) {
            // Saved position keys at attachment instants never override the
            // carrier endpoints. Dragging may have stored those snapshots.
            const from = dest ? carrierPos(dest, tN)
                : (ownKeyAt(tN)?.position || (prevDest && carrierPos(prevDest, tN)) || ownPos(tN));
            const to = nextDest ? carrierPos(nextDest, nextKey.time)
                : ownKeyAt(nextKey.time)?.position;
            if (from && to) return interpolate(from, to, tN, nextKey.time);
        }


        // ── DETACHED — the detach key is where the interpolation STARTS.
        //
        // The item's own keys cannot begin this stretch: they were laid down
        // around the release and the closest one may sit well behind it. So we
        // anchor on where the item actually was at the detach — the position key
        // the user placed at that instant if there is one, otherwise the carrier
        // it was riding — and interpolate from there to its next own key. With no
        // keyed landing yet, it simply stays where it was released, rather than
        // drifting back to an older key.
        if (!dest && prevDest) {
            const releaseKey = ownKeyAt(tN);
            const anchor = releaseKey
                ? releaseKey.position
                : (offsetPos(playerPos(prevDest, tN), forwardDir(prevDest, tN)) || null);
            if (anchor) {
                const nextOwn = ownAfter(tN)[0] || null;
                if (!nextOwn) return anchor;              // nothing keyed after the release
                if (t >= nextOwn.time) return ownPos(t);  // landed → back on its own path
                // The segment for the release → landing stretch: the user's own
                // pair when they keyed the release point, otherwise the pair the
                // landing belongs to — so a curve/arch set on that movement bends
                // the released item's flight too.
                const before = releaseKey
                    || (track.position || []).filter(k => k.time < tN - 1e-6).pop()
                    || null;
                const seg = findSegment(track, before ? before.time : tN, nextOwn.time);
                const f = Math.min(1, Math.max(0, (t - tN) / Math.max(1e-6, nextOwn.time - tN)));
                return interpolateWithSegment({ position: anchor }, nextOwn, f, seg);
            }
        }

        // At and after an attachment key, follow its carrier immediately.
        // The preceding interval has already delivered the ball to this point.
        if (dest) return carrierPos(dest, t) || ownPos(t);

    }
    return sampleTrackPosition(track, t);
}
